import { Entity, Field } from '@adornis/baseql/decorators.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { validate } from '@adornis/validation/decorators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { Adresse } from '@chancenwerk/db/adresse.js';
import type { Filter } from 'mongodb';
import { Mitarbeiter, Unterlagen } from './Mitarbeiter.js';

/**
 * @entity ExternerMitarbeiter
 */
@Entity()
export class ExternerMitarbeiter extends Mitarbeiter {
  public static override _class = 'ExternerMitarbeiter';

  public static override readableName = 'Mitarbeiter:in';

  @validate<Mitarbeiter>(({ value, key, source, step, target }) => {
    if ((target._class === 'VerwaltungsMitarbeiter' || target._class === 'Mitarbeiter') && !value)
      throw new ValidationError('ExternerMitarbeiter has to have vorname', { key, source });
  })
  @Field(type => String)
  public override vorname?: string;

  @validate<Mitarbeiter>(({ value, key, source, step, target }) => {
    if ((target._class === 'VerwaltungsMitarbeiter' || target._class === 'Mitarbeiter') && !value)
      throw new ValidationError('ExternerMitarbeiter has to have nachname', { key, source });
  })
  @Field(type => String)
  public override nachname?: string;

  public static get templateFields(): BaseQLSelectionSet<ExternerMitarbeiter> {
    return {
      username: 1,
      email: 1,
      vorname: 1,
      nachname: 1,
      handynummer: 1,
      employeeID: 1,
      geschlecht: 1,
      geburtstag: 1,
      adresse: Adresse.allFields,
      emailChancenwerk: 1,
      festnetz: 1,
      handynummerChancenwerk: 1,
      vertraege: {
        startDatum: 1,
        vertragsart: 1,
        taetigkeiten: {
          object: {
            school: {
              name: 1,
              kuerzel: 1,
            },
          },
          predicate: {
            _class: 1,
            _id: 1,
            name: 1,
          },
          einsaetzeProWoche: 1,
          aufwandsentschaedigung: 1,
          aufwandsentschaedigungTeamtreffen: 1,
          startDate: 1,
          endDate: 1,
        },
      },
      unterlagen: Unterlagen.allFields,
    };
  }
}

/**
 * Finds similar guest Mitarbeiter by vorname, nachname, email and handynummer. Email and handynummer are optional.
 * Vorname and nachname are required. The query returns up to 15 similar guest Mitarbeiter.
 *
 * @query findSimilarGuestMitarbeiter
 * @param similar - The similar ExternerMitarbeiter to search for
 */
export const findSimilarGuestMitarbeiter = registerQuery({
  type: () => [ExternerMitarbeiter],
  operationName: 'findSimilarGuestMitarbeiter',
  resolve: (similar: ExternerMitarbeiter) => {
    return async (gqlFields: BaseQLSelectionSet<ExternerMitarbeiter>) => {
      const col = await getRawCollection<ExternerMitarbeiter>(ExternerMitarbeiter._collectionName);

      // vorname and nachname are required
      if (!similar.vorname || !similar.nachname || similar.vorname === '' || similar.nachname === '') {
        throw new Error('vorname and nachname are required');
      }

      const query: Filter<ExternerMitarbeiter> = {
        $and: [{ vorname: similar.vorname }, { nachname: similar.nachname }, { guestAccount: true }],
      };

      if (similar.email) {
        query.$and?.push({ email: similar.email });
      }

      if (similar.handynummer) {
        query.$and?.push({ handynummer: similar.handynummer });
      }

      const res = await col
        .find(query, {
          projection: {
            _id: 1,
            _class: 1,
            vorname: 1,
            nachname: 1,
            email: 1,
            handynummer: 1,
          },
        })
        .limit(4)
        .toArray();

      return res as ExternerMitarbeiter[];
    };
  },
  options: {},
  params: [{ type: () => ExternerMitarbeiter, name: 'similar' }],
});
